import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import FooterSection from "../home/components/FooterSection";
import ChatbotWidget from "../home/components/ChatbotWidget";
import RouteHero from "./components/RouteHero";
import RouteCardItem from "./components/RouteCardItem";
import { cityRouteDetails } from "../../mocks/routeDetails";

export default function RoutesPage() {
  const [activeFilter, setActiveFilter] = useState("all");

  const filtered = useMemo(() => {
    if (activeFilter === "all") return cityRouteDetails;
    if (activeFilter === "easy" || activeFilter === "medium" || activeFilter === "hard") {
      return cityRouteDetails.filter((r) => r.difficulty === activeFilter);
    }
    return cityRouteDetails.filter((r) => r.category === activeFilter);
  }, [activeFilter]);

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      {/* Hero with filters */}
      <RouteHero activeFilter={activeFilter} onFilterChange={setActiveFilter} />

      {/* Quick Tips Bar */}
      <div className="bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex flex-wrap items-center gap-4 md:gap-8 text-xs text-stone-500">
          <div className="flex items-center gap-1.5">
            <i className="ri-lightbulb-line text-amber-500" />
            <span>Her rota üzerine tıklayarak adım adım durakları görebilirsiniz.</span>
          </div>
          <div className="flex items-center gap-1.5">
            <i className="ri-map-pin-line text-emerald-600" />
            <span>Durak isimlerine tıklayarak mekan detayına ulaşabilirsiniz.</span>
          </div>
          <div className="flex items-center gap-1.5 ml-auto">
            <i className="ri-route-line text-stone-400" />
            <span>{cityRouteDetails.length} hazır rota</span>
          </div>
        </div>
      </div>

      {/* Routes Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
        {/* Results row */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-playfair text-2xl font-bold text-stone-800">
              {activeFilter === "all" ? "Tüm Rotalar" : `"${activeFilter}" Rotaları`}
            </h2>
            <p className="text-stone-400 text-sm mt-1">
              {filtered.length} rota bulundu
            </p>
          </div>
          {activeFilter !== "all" && (
            <button
              onClick={() => setActiveFilter("all")}
              className="text-sm text-emerald-700 hover:text-emerald-800 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-circle-line" /> Filtreyi Kaldır
            </button>
          )}
        </div>

        {/* Route Cards */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl p-16 text-center border border-stone-100">
            <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-route-line text-2xl text-stone-400" />
            </div>
            <h3 className="font-bold text-stone-700 text-lg mb-2">Bu filtre için rota bulunamadı</h3>
            <button
              onClick={() => setActiveFilter("all")}
              className="text-emerald-700 hover:underline cursor-pointer text-sm font-medium"
            >
              Tüm rotalara dön
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {filtered.map((route, idx) => (
              <RouteCardItem key={route.id} route={route} index={idx} />
            ))}
          </div>
        )}

        {/* CTA section — Create own route */}
        <div className="mt-14 rounded-2xl overflow-hidden relative">
          <img
            src="https://public.readdy.ai/ai/img_res/6e86de6ffbe0be689207bdd7c38d846f.jpg"
            alt="Kendi Rotanı Oluştur"
            className="w-full h-52 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/85 to-stone-900/40 flex items-center px-8">
            <div className="max-w-lg">
              <h3 className="font-playfair text-white text-2xl font-bold mb-2">Kendi Rotanı Chatbot ile Oluştur</h3>
              <p className="text-white/65 text-sm mb-5 leading-relaxed">
                Sağ alttaki Bursa Asistanı'na ilgi alanlarını, sürenizi ve tercihinizi söyleyin — size özel rota hazırlasın.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-robot-line" /> Chatbot&apos;u Aç
                </Link>
                <Link
                  to="/category/historical"
                  className="bg-white/15 hover:bg-white/25 text-white px-5 py-2.5 rounded-xl text-sm font-medium border border-white/20 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Mekanları Keşfet
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Tips grid */}
        <section className="mt-14">
          <h2 className="font-playfair text-2xl font-bold text-stone-800 mb-6">Bursa'yı Gezerken Bilinmesi Gerekenler</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                icon: "ri-car-line",
                title: "Ulaşım",
                text: "Bursa'da toplu ulaşım var ama araba ile dolaşmak çok daha verimli. Teleferik ve Extrempark için araç şart.",
                color: "bg-emerald-50 text-emerald-700",
              },
              {
                icon: "ri-calendar-event-line",
                title: "En İyi Zaman",
                text: "Nisan-Haziran ve Eylül-Ekim ayları ideal. Cumalıkızık hafta sonu çok kalabalık, hafta içi tercih edin.",
                color: "bg-amber-50 text-amber-700",
              },
              {
                icon: "ri-hotel-line",
                title: "Konaklama",
                text: "Çekirge termal bölgesi en popüler konaklama alanı. Butik otel tercih edenler Tophane'yi düşünebilir.",
                color: "bg-stone-100 text-stone-700",
              },
              {
                icon: "ri-restaurant-line",
                title: "Yemek",
                text: "İskender Kebap öğle yemeği için, Cumalıkızık kahvaltısı sabah için ideal. Pide akşam seçeneği.",
                color: "bg-red-50 text-red-700",
              },
              {
                icon: "ri-camera-line",
                title: "Fotoğraf",
                text: "En iyi saatler: Sabah 7-9 (Cumalıkızık) ve akşam (Tophane). Teleferikten panoramik çekim harika.",
                color: "bg-violet-50 text-violet-700",
              },
              {
                icon: "ri-money-euro-circle-line",
                title: "Bütçe",
                text: "Pek çok tarihi yer ücretsiz. Extrempark + Teleferik + İskender ortalama 600-800 TL kişi başı.",
                color: "bg-sky-50 text-sky-700",
              },
            ].map((tip) => (
              <div key={tip.title} className="bg-white rounded-2xl p-5 border border-stone-100">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${tip.color} mb-4`}>
                  <i className={`${tip.icon} text-lg`} />
                </div>
                <h3 className="font-semibold text-stone-800 mb-2">{tip.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed">{tip.text}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <FooterSection />
      <ChatbotWidget />
    </div>
  );
}
